import React, { useEffect, useState, useMemo, useCallback, ReactNode } from "react";
import {
  Col,
  Container,
  Row,
  Card,
  CardHeader,
  CardBody,
  Input,
  Button,
} from "reactstrap";

import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import BreadCrumb from "Components/Common/BreadCrumb";
import Loader from "Components/Common/Loader";
import { createCourse, deletecourse, fetchCourses, updatecourse, updateCourseStatusThunk, fetchSkills, fetchKeySummary } from "slices/Course/thunk";
import { listSkill, listKeySummary } from "services/courseService";
import CourseContainer from "./courseContainer";
import type { ThunkDispatch } from "redux-thunk";
import type { AnyAction } from "redux";
import type { RootState } from "store";
import { Link } from "react-router-dom";
import { Modal, ModalHeader, ModalBody } from "reactstrap";
import CourseForm from "./CourseForm";
import { useFormik } from "formik";
import * as Yup from "yup";
import AWS from "aws-sdk";
import { toast } from "react-toastify";
import CourseTabs from "./CourseTabs";


const selectCourseState = (state: RootState) => (state as any).course;

const courseDataSelector = createSelector(selectCourseState, (course) => ({
  courseData: course.courseData,
  loading: course.loading,
  pagination: course.pagination,
}));

const CoursePage = () => {
  const dispatch: ThunkDispatch<RootState, any, AnyAction> = useDispatch();
  const { courseData, loading, pagination } = useSelector(courseDataSelector);

  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [rowID, setRowId] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [editCourseData, setEditCourseData] = useState<any>(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedCourseId, setSelectedCourseId] = useState<number | null>(null);
  const [updatingStatusId, setUpdatingStatusId] = useState<number | null>(null);

  const handleStatusToggle = useCallback(
    async (course_id: number, status: string) => {
      setUpdatingStatusId(course_id);
      try {
        const res: any = await dispatch(updateCourseStatusThunk(course_id, { status }));
        if (res?.success) {
          toast.success(res?.message || "Status updated successfully");
          dispatch(
            fetchCourses({
              page,
              per_page: limit,
              from_date: "",
              to_date: "",
              course_id: 0,
              searchkey: searchQuery,
            })
          );
        } else {
          toast.error(res?.message || "Failed to update status");
        }
      } catch {
        toast.error("Failed to update status");
      } finally {
        setUpdatingStatusId(null);
      }
    },
    [dispatch, page, limit, searchQuery]
  );

  useEffect(() => {
    const handler = setTimeout(() => {
      dispatch(
        fetchCourses({
          page,
          per_page: limit,
          from_date: "",
          to_date: "",
          course_id: 0,
          searchkey: searchQuery,
        })
      );
    }, 500);
    return () => clearTimeout(handler);
  }, [dispatch, page, limit, searchQuery]);

  useEffect(() => {
    setPage(1);
  }, [searchQuery]);

 const handlePageChange = (newPage: number) => setPage(newPage);

const validation = useFormik({
  initialValues: {
    course_name: "",
    description: "",
    duration: "",
    course_level: "",
    trainer: "",
    price: "",
    discount: "",
    thumbnail_url: "",
    noofmodule: "",
    hourperday: "",
    noofweek: "",
    remarks: "",
    course_id: "",
    syllabus_id: "",
    startdate: new Date().toISOString().split("T")[0], 
    key_deliverables: "",
    daywise_description: "", 
    skills: [],
    summaries: [],
    syllabus_items: [],
    weekid: "",
    week_title: "",
    week_keydeliverable: "",
    dayid: "",
    day_title: "",
    day_description: "",
  },

  validationSchema: Yup.object({
    course_name: Yup.string()
      .trim()
      .required("Course name is required")
      .matches(/^[A-Za-z _-]+$/, "Only alphabets, underscore and hyphens allowed")
      .test("wordCount", "Words length must not exceed 70", (val) => {
        if (!val || !val.trim()) return true;
        const words = val.trim().split(/\s+/).filter(Boolean);
        if (words.length > 70) return false;
        return true;
      }),

    trainer: Yup.string()
      .trim()
      .required("Trainer name is required")
      .matches(/^[A-Za-z _-]+$/, "Trainer name must contain only alphabets")
      .test("wordCount", "Words length must not exceed 70", (val) => {
        if (!val || !val.trim()) return true;
        const words = val.trim().split(/\s+/).filter(Boolean);
        if (words.length > 70) return false;
        return true;
      }),

    description: Yup.string()
      .trim()
      .required("Description is required")
      .test("wordCount", "Words length must not exceed 250", (val) => {
        if (!val || !val.trim()) return true;
        const words = val.trim().split(/\s+/).filter(Boolean);
        if (words.length > 250) return false;
        return true;
      }),

    thumbnail_url: Yup.string().trim().required("Cover Image is required"),

    duration: Yup.string()
      .trim()
      .matches(/^\d{1,8}(\.\d{1,3})?$/, "Only numbers allowed")
      .required("Duration is required"),

    noofmodule: Yup.string()
      .trim()
      .matches(/^[0-9]{1,9}$/, "Only numbers allowed")
      .required("No. of modules required"),

    hourperday: Yup.string()
      .trim()
      .matches(/^[0-9]{0,9}$/, "Only numbers allowed")
      .optional(),

    noofweek: Yup.string()
      .trim()
      .matches(/^[0-9]{0,9}$/, "Only numbers allowed")
      .optional(),

    course_level: Yup.string()
      .trim()
      .matches(/^[A-Za-z ]+$/, "Only alphabets allowed")
      .required("Select a course level"),

    price: Yup.string()
      .trim()
      .matches(/^\d{1,8}(\.\d{1,3})?$/, "Only numbers allowed")
      .required("Price is required"),

    discount: Yup.string()
      .trim()
      .matches(/^\d{0,8}(\.\d{1,3})?$/, "Up to 8 digits, 3 decimals")
      .optional(),
  }),

  onSubmit: async (values, { resetForm }) => {
    try {

    if (isEdit && editCourseData) {
     await dispatch(updatecourse(editCourseData.id, values));
     toast.success("Course Updated successfully!");

    } else {
      await dispatch(createCourse(values));
       toast.success("Course added successfully!");
    }

      dispatch(
        fetchCourses({
          page,
          per_page: limit,
          from_date: "",
          to_date: "",
          course_id: 0,
          searchkey: searchQuery,
        })
      );

      resetForm();
      setIsEdit(false);
      setEditCourseData(null);
  } catch (error) {
    console.error("Error saving course:", error);
  }
},
});

const toggleModal = () => {
 setIsModalOpen((prev) => {
   if (prev) {                    
     validation.resetForm();
     setIsEdit(false);
     setEditCourseData(null);
   }
   return !prev;
 });
};

const handleWizardClose = async () => {
  setIsModalOpen(false);
  validation.resetForm();
  setIsEdit(false);
  setEditCourseData(null);

  await dispatch(
    fetchCourses({
      page,
      per_page: limit,
      from_date: "",
      to_date: "",
      course_id: 0,
      searchkey: searchQuery,
    })
  );
};

const handleUserClick = useCallback(
  async (row: any) => {   
    setIsEdit(true);
    setEditCourseData(row);
    setIsModalOpen(true);

    const courseId = row.id || row.course_id;

    validation.setValues({
      course_id: courseId || "",
      syllabus_id:
        row.syllabus_id ||
        row.syllabus_items?.[0]?.syllabus_id ||
        row.syllabus_items?.[0]?.id ||
        "",
      course_name: row.course_name || "",
      description: row.description || "",
      duration: row.duration || "",
      course_level: row.course_level || "",
      trainer: row.trainer || "",
      price: row.price || "",
      discount: row.discount || "",
      thumbnail_url: row.thumbnail_url || "",
      noofmodule: row.module || row.noofmodule || "",
      remarks: row.remarks || "",
      hourperday: row.hourperday || "",
      noofweek: row.noofweek || "",
      startdate: row.startdate || new Date().toISOString().split("T")[0],
      key_deliverables: row.key_deliverables ||"",
      daywise_description: row.daywise_description ||"", 
      skills: row.skills || [],
      summaries: row.summaries || [],
      syllabus_items: row.syllabus_items || [],
      weekid: "",
      week_title: "",
      week_keydeliverable: "",
      dayid: "",
      day_title: "",
      day_description: "",
    });

    if (courseId) {
      try {
        const [skillsResponse, keySummaryResponse] = await Promise.all([
          listSkill(courseId).catch(err => ({ success: false, data: [], message: err?.message })),
          listKeySummary(courseId).catch(err => ({ success: false, data: [], message: err?.message }))
        ]);
        dispatch(fetchSkills(courseId));
        dispatch(fetchKeySummary(courseId));
        const updates: any = {};

        if (skillsResponse?.success && skillsResponse?.data) {
          updates.skills = Array.isArray(skillsResponse.data) ? skillsResponse.data : [];
        }

        if (keySummaryResponse?.success && keySummaryResponse?.data) {
          const summariesData = Array.isArray(keySummaryResponse.data) 
            ? keySummaryResponse.data.map((item: any) => ({
                headline: item.head_line || item.headline || "",
                summaryText: item.keysummary || item.summaryText || "",
                iconPreview: item.image_path || item.iconPreview || "",
                iconFile: null,
                isSaved: true, 
                id: item.id, 
              }))
            : [];
          updates.summaries = summariesData;
        }

        if (Object.keys(updates).length > 0) {
          validation.setValues((prev: any) => ({
            ...prev,
            ...updates
          }));
        }
      } catch (error: any) {
        console.error("Error fetching course details:", error);
      }
    }
  },
  [validation, dispatch]
);

const columns = useMemo(() => [
  {
    header: "Course ID",
    accessorKey: "id",
    enableColumnFilter: false,
     cell: (cell: any) => {
    const id = cell.getValue(); 
    return <strong>{id}</strong>;
  },
  },
  {
    header: "Subject",
    accessorKey: "course_name",
    enableColumnFilter: false,
    cell: (cell: any) => (
      <span
        style={{
          display: "-webkit-box",
          WebkitLineClamp: 3,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
          textOverflow: "ellipsis",
          wordBreak: "break-word",
          lineHeight: "1.4",
          maxWidth: "300px",
        }}
      >
        {cell.getValue() || "N/A"}
      </span>
    ),
  },
  {
    header: "Module",
    accessorKey: "module",
    enableColumnFilter: false,
    cell: (cell: any) => `${cell.getValue()} `,
  },
  {
    header: "Trainer",
    accessorKey: "trainer",
    enableColumnFilter: false,
    cell: (cell: any) => cell.getValue() || "N/A",
  },
  {
    header: "Hours",
    accessorKey: "duration",
    enableColumnFilter: false,
    cell: (cell: any) => `${cell.getValue()} `,
  },
  {
    header: "Status",
    accessorKey: "status",
    enableColumnFilter: false,
    cell: (cell: any) => {
      const row = cell.row.original;
      const course_id = row.id;
      const value = (row.status || row.remarks || "Active").trim();
      const displayValue = value ? value.charAt(0).toUpperCase() + value.slice(1).toLowerCase() : "Active";
      const lowerVal = displayValue.toLowerCase();
      const statusColor = lowerVal === "active" ? "#159E3A" : "#A65F00";
      const isUpdating = updatingStatusId === course_id;

      return (
        <select
          className="form-select form-select-sm"
          value={displayValue}
          disabled={isUpdating}
          style={{
            width: "120px",
            border: `1px solid ${statusColor}`,
            borderRadius: "20px",
            color: statusColor,
            padding: "4px 20px",
            height: "26px",
            fontSize: "12px",
            textAlign: "center",
          }}
          onChange={(e) => {
            e.stopPropagation();
            handleStatusToggle(course_id, e.target.value);
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <option value="Active">Active</option>
          <option value="Inactive">Inactive</option>
        </select>
      );
    },
  },

  {
    header: "Level",
    accessorKey: "course_level",
    enableColumnFilter: false,
  },
  {
  header: "Description",
  accessorKey: "description",
  enableColumnFilter: false,
  cell: (cell: any) => (
    <span
      style={{
        whiteSpace: "normal",
        wordBreak: "break-word",
        overflowWrap: "break-word",
        maxWidth: "300px",   // adjust as needed
        display: "block",
      }}
    >
      {cell.getValue() || "N/A"}
    </span>
  ),
},

  {
    header: "Actions",
    accessorKey: "action",
    enableColumnFilter: false,
    cell: (cellProps: any) => {
      const course = cellProps.row.original;

      return (
        <ul className="list-inline hstack gap-1 mb-0">
          {/* <li className="list-inline-item" title="Edit">
          <Button
            color="primary"
            className="btn btn-icon btn-soft-primary btn-sm"
            onClick={(e) => {
              e.stopPropagation();
              handleUserClick(course);
            }}
          >
            <i className="ri-pencil-fill"></i>
          </Button> 
        </li> */}
          <li className="list-inline-item" title="Delete">
          <Link
            className="btn btn-icon btn-soft-danger btn-sm"
            to="#"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setSelectedCourseId(course.id); 
              setDeleteModal(true); 
            }}
          >
            <i className="ri-delete-bin-5-line"></i>
          </Link>
        </li>
        </ul>
      );
    },
  }
], [handleStatusToggle, updatingStatusId]);

  const handleRowClick = useCallback((row: any) => {
    setRowId(row.id || null);
  }, []);

  document.title = "AI Module | Courses";

  return (
    <div className="page-content">
      <Container fluid className="border pt-4 rounded-4 bg-light mb-3 ms-3 pe-4">
        <BreadCrumb title="AI Module" pageTitle={["AI", "Courses"]} />

        <Row className="align-items-center g-3 d-flex justify-content-between pb-4 pe-3">
          <Col md={2}>
            <div className="position-relative">
              <Input
                type="text"
                className="form-control border ps-5 ms-2 rounded-3"
                value={""}
                placeholder="Filter"
                disabled
              />
              <i
                className="ri-filter-line position-absolute top-50 start-0 translate-middle-y text-muted ms-3"
                style={{ fontSize: "1.1rem" }}
              />
            </div>
          </Col>
          <Col md={3}>
            <div className="position-relative">
              <Input
                type="text"
                className="form-control border ps-5 ms-2 rounded-3"
                value={searchQuery}
                placeholder="Search Course..."
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <i
                className="ri-search-line position-absolute top-50 start-0 translate-middle-y text-muted ms-3"
                style={{ fontSize: "1.1rem" }}
              />
            </div>
          </Col>
          <Col
            md={7}
            className="text-end d-flex justify-content-end gap-2 align-items-center"
          >
            <Button color="primary" outline tag={Link} to="/lead">
              <i className="ri-group-line align-middle me-1" /> View Leads
            </Button>
         <Button color="primary" onClick={toggleModal}>
          + Add New
        </Button>  
          </Col>
        </Row>

        <Row>
          <Col lg={12}>
            <Card
              id="coursesList"
              className="border-0 shadow-none"
              style={{ border: "none" }}
            >
              {/* <CardHeader className="border bg-primary-subtle">
                <strong>Course List</strong>
              </CardHeader> */}
              <CardBody className="pt-3" style={{ minHeight: "500px" }}>
                <div className="tab-content">
                  {!loading ? (
                    courseData?.length > 0 ? (
                      <CourseContainer
                  
                        data={courseData}
                        pagination={pagination}
                        limit={limit}
                        setLimit={setLimit}
                        setPage={handlePageChange}   
                        onPageChange={handlePageChange}
                        setRowId={setRowId}
                        onRowClick={handleRowClick}
                        tableClass="align-middle border-bottom rounded-pill"
                        theadClass="bg-primary text-white border-top border-success"
                        trClass=""
                        divClass="table-responsive table-card"
                        SearchPlaceholder="Search courses..." columns={columns}                       />
                    ) : (
                      <div className="text-center p-4">No records found.</div>
                    )
                  ) : (
                    <Loader />
                  )}
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    <Modal isOpen={isModalOpen} toggle={toggleModal} centered size="lg">
      <ModalHeader toggle={toggleModal}>
        {isEdit ? "Edit Course" : "Add New Course"}
      </ModalHeader>
      <ModalBody>
        <CourseTabs validation={validation} isEdit={isEdit} onClose={handleWizardClose} />
      </ModalBody>
    </Modal>
    <Modal isOpen={deleteModal} toggle={() => setDeleteModal(!deleteModal)} centered>
    <ModalHeader toggle={() => setDeleteModal(!deleteModal)}>
      Confirm Delete
      </ModalHeader>
      <ModalBody className="text-center">
      <h5>Are you sure you want to delete this course?</h5>
      <p className="text-muted">
        This action cannot be undone.
      </p>
      <div className="d-flex justify-content-center gap-3 mt-4">
        <Button
          color="secondary"
          onClick={() => setDeleteModal(false)}
        >
          Cancel
        </Button>
        <Button
          color="danger"
          onClick={() => {
            if (selectedCourseId) {
              setDeleteModal(false);
              const courseId = selectedCourseId;
              setSelectedCourseId(null);
              setTimeout(async () => {
                await dispatch(deletecourse(courseId));
                toast.success("Course Deleted successfully!");
                await dispatch(
                  fetchCourses({
                    page,
                    per_page: limit,
                    from_date: "",
                    to_date: "",
                    course_id: 0,
                    searchkey: searchQuery,
                  })
                );
              }, 0);
            }
          }}
        > Delete
        </Button>
      </div>
    </ModalBody>
  </Modal>
    </div>
  );
};

export default CoursePage;
